/**
 * AI Visibility Schema Component
 * Adds page-specific structured data for improved AI/LLM visibility (GEO/AEO):
 * - BreadcrumbList schema for all content pages
 * - MedicalWebPage schema for health content pages
 * - HowTo schema for treatment process descriptions
 * - ItemList schema for index/listing pages
 * 
 * This component is rendered per-page (not globally) to provide route-specific schemas.
 */

const BASE_URL = "https://bellaroseclinics.ae";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

interface AIVisibilitySchemaProps {
  /** Page type determines which schemas are rendered */
  pageType: "home" | "services" | "blog-index" | "blog-article" | "guide-index" | "guide" | "faq-index" | "faq-service" | "news-index" | "news-article" | "offers" | "reviews" | "why-bella-rose";
  /** Breadcrumb trail for the current page */
  breadcrumbs?: BreadcrumbItem[];
  /** Page title for MedicalWebPage */
  pageTitle?: string;
  /** Page description for MedicalWebPage */
  pageDescription?: string;
  /** Date published (ISO string) */
  datePublished?: string;
  /** Date modified (ISO string) */
  dateModified?: string;
  /** Author name for medical content */
  authorName?: string;
  /** Named reviewer only after explicit approval for this exact page */
  reviewerName?: string;
  /** ISO review date recorded with the explicit approval */
  reviewedDate?: string;
  /** HowTo steps for treatment pages */
  howToSteps?: HowToStep[];
  /** HowTo name */
  howToName?: string;
  /** HowTo description */
  howToDescription?: string;
  /** FAQ items for FAQ schema */
  faqItems?: Array<{ question: string; answer: string }>;
  /** Keywords for the page */
  keywords?: string[];
  /** Specialty area */
  specialty?: string;
}

export function AIVisibilitySchema({
  pageType,
  breadcrumbs,
  pageTitle,
  pageDescription,
  datePublished,
  dateModified,
  authorName,
  reviewerName,
  reviewedDate,
  howToSteps,
  howToName,
  howToDescription,
  faqItems,
  keywords,
  specialty,
}: AIVisibilitySchemaProps) {
  const schemas: object[] = [];

  // 1. BreadcrumbList schema (all pages except home)
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url}`,
      })),
    });
  }

  // 2. MedicalWebPage schema (for health content pages)
  const medicalPageTypes = ["blog-article", "guide", "news-article", "services", "faq-service"];
  if (medicalPageTypes.includes(pageType) && pageTitle) {
    const medicalWebPage: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      "@id": `${BASE_URL}/#${pageType}-${pageTitle?.replace(/\s+/g, "-").toLowerCase().slice(0, 50)}`,
      name: pageTitle,
      description: pageDescription || pageTitle,
      url: typeof window !== "undefined" ? window.location.href : BASE_URL,
      inLanguage: ["en", "ar"],
      isPartOf: { "@id": `${BASE_URL}/#website` },
      about: {
        "@type": "MedicalCondition",
        name: specialty || "General Health",
      },
      mainContentOfPage: {
        "@type": "WebPageElement",
        cssSelector: "main, article, .content",
      },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "h2", ".summary", ".key-takeaway", "blockquote"],
      },
    };

    if (reviewerName && reviewedDate) {
      medicalWebPage.lastReviewed = reviewedDate;
      medicalWebPage.reviewedBy = {
        "@type": "Person",
        name: reviewerName,
        worksFor: { "@id": `${BASE_URL}/#medicalclinic` },
      };
    }

    if (datePublished) medicalWebPage.datePublished = datePublished;
    if (dateModified) medicalWebPage.dateModified = dateModified;
    if (authorName) {
      medicalWebPage.author = {
        "@type": "Physician",
        name: authorName,
        worksFor: { "@id": `${BASE_URL}/#medicalclinic` },
        memberOf: {
          "@type": "GovernmentOrganization",
          name: "Dubai Health Authority",
        },
      };
    }
    if (keywords && keywords.length > 0) {
      medicalWebPage.keywords = keywords.join(", ");
    }

    schemas.push(medicalWebPage);
  }

  // 3. HowTo schema (for treatment process descriptions)
  if (howToSteps && howToSteps.length > 0 && howToName) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: howToName,
      description: howToDescription || `Step-by-step guide for ${howToName}`,
      totalTime: "PT60M",
      tool: [
        { "@type": "HowToTool", name: "Medical consultation" },
        { "@type": "HowToTool", name: "DHA-licensed specialist" },
      ],
      step: howToSteps.map((step, index) => ({
        "@type": "HowToStep",
        position: index + 1,
        name: step.name,
        text: step.text,
        ...(step.image ? { image: step.image } : {}),
      })),
      performedBy: {
        "@type": "Organization",
        name: "Bella Rose Medical Center",
        url: BASE_URL,
      },
    });
  }

  // 4. FAQ schema (for pages with FAQ content)
  if (faqItems && faqItems.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.slice(0, 10).map(item => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }

  // 5. ItemList schema (for index/listing pages)
  const indexPageTypes = ["blog-index", "guide-index", "news-index"];
  if (indexPageTypes.includes(pageType) && pageTitle) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: pageTitle,
      description: pageDescription || `Browse ${pageTitle}`,
      url: typeof window !== "undefined" ? window.location.href : BASE_URL,
      isPartOf: { "@id": `${BASE_URL}/#website` },
      provider: { "@id": `${BASE_URL}/#medicalclinic` },
    });
  }

  if (schemas.length === 0) return null;

  return (
    <>
      {schemas.map((schema, i) => (
        <script
          key={`ai-vis-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
